import React, { useState, useCallback, useEffect, useRef } from "react";
import { LoadScript } from "@react-google-maps/api";
import "./App.css";
import AppBar from "./components/AppBar";
import LoadingOverlay from "./components/LoadingOverlay";
import MainContent from "./components/MainContent";
import DebugTools from "./components/DebugTools";
import TreasurePrompt from "./components/TreasurePrompt";
import FabGroup from "./components/FabGroup";
import TreasureExtraModal from "./components/TreasureExtraModal";
import { useUserData } from "./UserDataContext";
import { uploadTreasure } from "./utils/treasure";
import { getAuth } from "firebase/auth";

// --- Google Maps 設定 ---
const libraries = ["geometry"];
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";
const containerStyle = { width: "100vw", height: "100vh" };
const defaultCenter = { lat: 22.3193, lng: 114.1694 };
const mapStyles = [
  { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#444" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
  {
    featureType: "administrative",
    elementType: "geometry",
    stylers: [{ visibility: "off" }],
  },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#e0e0e0" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#d6d6d6" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#888" }],
  },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#c9e6ff" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#b1b1b1" }],
  },
];

// --- API Base URL 設定 ---
// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

function App() {
  // --- 地圖與定位狀態 ---
  const [map, setMap] = useState(null);
  const [mapKey, setMapKey] = useState(Date.now());
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [originLat, setOriginLat] = useState("22.3193");
  const [originLng, setOriginLng] = useState("114.1694");
  const [userLat, setUserLat] = useState(null);
  const [userLng, setUserLng] = useState(null);
  const [heading, setHeading] = useState(0);
  const [bounds, setBounds] = useState(null);

  // --- 路線與地點狀態 ---
  const [routes, setRoutes] = useState([]);
  const [directions, setDirections] = useState(null);
  const [places, setPlaces] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [duration, setDuration] = useState("30");

  // --- UI 狀態 ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showControls, setShowControls] = useState(true);
  const [showNavigation, setShowNavigation] = useState(false);
  const [navStep, setNavStep] = useState(0);
  const [showTreasurePrompt, setShowTreasurePrompt] = useState(false);
  const [treasurePlace, setTreasurePlace] = useState(null); // 新增藏寶地點
  const [openFabGroup, setOpenFabGroup] = useState(false); // FabGroup 展開狀態
  const [randomRestaurant, setRandomRestaurant] = useState(null); // 新增隨機餐廳狀態
  const [showTreasureSuccess, setShowTreasureSuccess] = useState(false); // 新增藏寶成功提示狀態
  const [showTreasureExtra, setShowTreasureExtra] = useState(false);

  // --- 其他狀態 ---
  const abortControllerRef = useRef(null);
  const [geoError, setGeoError] = useState(null);
  const [geoRetryKey, setGeoRetryKey] = useState(0);
  const [hasFetchedPlaces, setHasFetchedPlaces] = useState(false);
  const [nearbyTreasures, setNearbyTreasures] = useState([]); // 新增附近寶藏狀態

  // --- 路線查詢邏輯 ---
  const handleSetRoute = useCallback(
    async (routeConfig) => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;
      setLoading(true);
      setError(null);
      setRoutes([]);
      setDirections(null);
      setSelectedPlace(null);
      setShowControls(false);
      if (routeConfig.type === "destination" && routeConfig.destination) {
        setSelectedPlace(routeConfig.destination);
      } else {
        setSelectedPlace(null);
      }
      setMapKey(Date.now());
      try {
        let url,
          body,
          isRandomWalk = false;
        if (routeConfig.type === "random") {
          isRandomWalk = true;
          url = `${API_BASE_URL}/api/random-walk`;
          body = JSON.stringify({
            origin_lat: parseFloat(originLat),
            origin_lng: parseFloat(originLng),
            duration: parseInt(duration, 10),
            ...(routeConfig.end_lat && routeConfig.end_lng
              ? { end_lat: routeConfig.end_lat, end_lng: routeConfig.end_lng }
              : {}),
          });
        } else if (routeConfig.type === "destination") {
          url = `${API_BASE_URL}/api/navigation/directions`;
          const params = new URLSearchParams({
            origin_lat: userLat,
            origin_lng: userLng,
            dest_lat: routeConfig.dest_lat,
            dest_lng: routeConfig.dest_lng,
          });
          url = `${url}?${params.toString()}`;
        } else {
          throw new Error("Invalid route type specified");
        }
        const response = await fetch(url, {
          method: isRandomWalk ? "POST" : "GET",
          headers: { "Content-Type": "application/json" },
          ...(isRandomWalk && { body }),
          signal,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Backend server error");
        let newRoutes = [];
        if (isRandomWalk) {
          if (data.directions_result) {
            const path =
              window.google.maps.geometry.encoding.decodePath(
                data.directions_result.routes[0].overview_polyline.points
              ) || [];
            newRoutes = [
              {
                path,
                total_distance: data.total_distance,
                total_duration: data.total_duration,
                navigation: data.navigation,
              },
            ];
            if (data.bounds) setBounds(data.bounds);
          }
        } else {
          if (
            data.status === "success" &&
            data.routes &&
            data.routes.length > 0
          ) {
            newRoutes = data.routes;
            if (data.bounds) setBounds(data.bounds);
          }
        }
        if (newRoutes.length > 0) setRoutes(newRoutes);
        else throw new Error("Backend did not return valid route data");
      } catch (err) {
        if (err.name === "AbortError") setError("Route generation cancelled");
        else setError(err.message);
        setShowControls(true);
      } finally {
        setLoading(false);
        abortControllerRef.current = null;
      }
    },
    [originLat, originLng, duration, userLat, userLng]
  );

  // --- 地圖自動 fitBounds ---
  useEffect(() => {
    if (map && bounds) {
      try {
        const { north, south, east, west } = bounds;
        if (north && south && east && west) {
          const mapBounds = new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(south, west),
            new window.google.maps.LatLng(north, east)
          );
          map.fitBounds(mapBounds);
        }
      } catch (error) {
        console.error("Error processing bounds:", error);
      }
    }
  }, [map, bounds]);

  // --- 定位副作用 ---
  useEffect(() => {
    let didSetDefault = false;
    let timeoutId;
    if (navigator.geolocation) {
      timeoutId = setTimeout(() => {
        if (!didSetDefault) {
          setOriginLat(defaultCenter.lat.toString());
          setOriginLng(defaultCenter.lng.toString());
          setMapCenter(defaultCenter);
          setUserLat(defaultCenter.lat);
          setUserLng(defaultCenter.lng);
          setGeoError("定位逾時，請檢查定位權限或網路");
          didSetDefault = true;
        }
      }, 10000);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (!didSetDefault) {
            clearTimeout(timeoutId);
            const { latitude, longitude } = position.coords;
            setOriginLat(latitude.toString());
            setOriginLng(longitude.toString());
            setMapCenter({ lat: latitude, lng: longitude });
            setUserLat(latitude);
            setUserLng(longitude);
            setGeoError(null);
            didSetDefault = true;
          }
        },
        (err) => {
          if (!didSetDefault) {
            clearTimeout(timeoutId);
            if (err.code === err.PERMISSION_DENIED) {
              setGeoError(
                "請允許定位權限，否則將顯示預設地點。您可至瀏覽器設定開啟定位權限。"
              );
            } else {
              setGeoError("定位失敗，已顯示預設地點");
            }
            setOriginLat(defaultCenter.lat.toString());
            setOriginLng(defaultCenter.lng.toString());
            setMapCenter(defaultCenter);
            setUserLat(defaultCenter.lat);
            setUserLng(defaultCenter.lng);
            didSetDefault = true;
          }
        }
      );
    } else {
      setOriginLat(defaultCenter.lat.toString());
      setOriginLng(defaultCenter.lng.toString());
      setMapCenter(defaultCenter);
      setUserLat(defaultCenter.lat);
      setUserLng(defaultCenter.lng);
      setGeoError("瀏覽器不支援定位，已顯示預設地點");
    }
    return () => clearTimeout(timeoutId);
  }, [geoRetryKey]);

  // --- 取得附近地點 ---
  useEffect(() => {
    const isDefaultCenter =
      mapCenter.lat === defaultCenter.lat &&
      mapCenter.lng === defaultCenter.lng;
    if (
      !hasFetchedPlaces &&
      !isDefaultCenter &&
      mapCenter.lat &&
      mapCenter.lng
    ) {
      async function fetchPlaces() {
        const center = mapCenter;
        const radius = 2000;
        const url = `${API_BASE_URL}/api/nearby-places?lat=${center.lat}&lng=${center.lng}&radius=${radius}&type=tourist_attraction|point_of_interest`;
        try {
          const resp = await fetch(url);
          const data = await resp.json();
          if (data.results) setPlaces(data.results.slice(0, 12));
          else setPlaces([]);
        } catch (e) {
          setPlaces([]);
          console.error("Failed to fetch nearby places", e);
        }
      }
      fetchPlaces();
      setHasFetchedPlaces(true);
    }
  }, [mapCenter, hasFetchedPlaces]);

  // --- 錯誤提示自動消失 ---
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // --- 地圖載入/卸載 ---
  const onLoad = useCallback((map) => setMap(map), []);
  const onUnmount = useCallback((_map) => setMap(null), []);

  // --- 產生隨機路線 ---
  const handleGenerateRoute = useCallback(
    (customEndLat, customEndLng) => {
      const config = {
        type: "random",
        ...(customEndLat &&
          customEndLng && { end_lat: customEndLat, end_lng: customEndLng }),
      };
      handleSetRoute(config);
    },
    [handleSetRoute]
  );

  // --- 取消產生路線 ---
  const handleCancelGenerate = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setShowControls(true);
    }
  };

  // --- 取得地點圖片 ---
  function getPlacePhotoUrl(place, maxWidth = 320) {
    // 後端自訂欄位
    if (place.photoUrl) {
      console.log("[getPlacePhotoUrl] 使用 photoUrl:", place.photoUrl);
      return place.photoUrl;
    }
    // Google Places API 格式
    if (place.photos && place.photos.length > 0) {
      const ref = place.photos[0].photo_reference;
      const url = `https://maps.googleapis.com/maps/api/place/photo?maxwidth=${maxWidth}&photoreference=${ref}&key=${GOOGLE_MAPS_API_KEY}`;
      console.log("[getPlacePhotoUrl] 使用 Google photo_reference:", url);
      return url;
    }
    // 預設圖片
    console.log("[getPlacePhotoUrl] 使用預設圖片");
    return "/logo192.png";
  }

  // --- 持續追蹤用戶位置 ---
  useEffect(() => {
    let watchId;
    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          setUserLat(pos.coords.latitude);
          setUserLng(pos.coords.longitude);
        },
        (err) => {
          console.warn(`Geolocation watch error: ${err.message}`);
          if (
            err.code === err.TIMEOUT ||
            err.code === err.POSITION_UNAVAILABLE
          ) {
            setUserLat(defaultCenter.lat);
            setUserLng(defaultCenter.lng);
            setError("定位失敗，已顯示預設地點");
          }
        },
        { enableHighAccuracy: true, maximumAge: 1000, timeout: 10000 }
      );
    }
    return () => {
      if (watchId && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, []);

  // --- 持續追蹤裝置方向 ---
  useEffect(() => {
    function handleOrientation(event) {
      if (typeof event.alpha === "number") {
        // 修正：反轉 180 度，讓 0=北、90=東、180=南、270=西
        setHeading((Math.round(event.alpha) + 180) % 360);
      }
    }
    if (window.DeviceOrientationEvent) {
      window.addEventListener("deviceorientation", handleOrientation, true);
    }
    return () => {
      if (window.DeviceOrientationEvent) {
        window.removeEventListener(
          "deviceorientation",
          handleOrientation,
          true
        );
      }
    };
  }, []);

  // --- 導航步驟 debug ---
  useEffect(() => {
    if (showNavigation) {
      const navSteps = routes[0]?.navigation?.steps || [];
      console.log("[DEBUG] 啟用導航，步驟數：", navSteps.length, navSteps);
    }
  }, [showNavigation, routes]);

  // --- UI 狀態 ---
  const shouldShowControls = showControls;
  const showGeoLoading = userLat === null && userLng === null;

  // --- 渲染 ---
  const userData = useUserData(); // 你可改成實際取得用戶資料
  // const userData = null; // TODO: 請改成你的用戶資料取得方式
  const isLoggedIn = !!userData;

  // FabGroup 藏寶按鈕 callback
  const handleFabTreasure = useCallback((userLocation) => {
    setOpenFabGroup(false); // 藏寶icon按下FabGroup收合
    console.log("[FabGroup] onTreasure callback", userLocation);
    if (userLocation && userLocation.lat && userLocation.lng) {
      setTreasurePlace({
        name: "我的位置",
        geometry: {
          location: { lat: userLocation.lat, lng: userLocation.lng },
        },
        isUserLocation: true,
        timestamp: new Date().toISOString(),
      });
      setShowTreasurePrompt(true);
      console.log("[App] setShowTreasurePrompt(true)", {
        treasurePlace: {
          name: "我的位置",
          geometry: {
            location: { lat: userLocation.lat, lng: userLocation.lng },
          },
          isUserLocation: true,
          timestamp: new Date().toISOString(),
        },
      });
    } else {
      console.log(
        "[FabGroup] onTreasure callback: userLocation 無效",
        userLocation
      );
    }
  }, []);

  const handleConfirmTreasure = () => {
    console.log("[藏寶提示] 用戶確認藏寶:", treasurePlace);
    setShowTreasureExtra(true); // 顯示藏寶附加資訊 modal
    setShowTreasurePrompt(false);
  };

  // 處理藏寶附加資訊送出
  const handleTreasureExtraSubmit = async (photo, desc) => {
    setShowTreasureExtra(false);
    setShowTreasureSuccess(true); // 顯示成功動畫/提示
    setTreasurePlace(null);
    setTimeout(() => {
      setShowTreasureSuccess(false);
      setOpenFabGroup(true); // 自動展開 FabGroup
    }, 1800);

    // 取得 Firebase userId
    const auth = getAuth();
    const userId = auth.currentUser?.uid || "anonymous";

    // 呼叫 utils/treasure.js 的 uploadTreasure
    try {
      await uploadTreasure(photo, desc, treasurePlace, API_BASE_URL, userId);
      console.log("[藏寶] 上傳成功");
    } catch (err) {
      console.error("[藏寶] 上傳失敗", err);
    }
  };

  const handleTreasureExtraCancel = () => {
    setShowTreasureExtra(false);
    setTreasurePlace(null);
    setOpenFabGroup(true);
  };

  // --- 隨機選擇餐廳（改為呼叫後端 API） ---
  const handleRandomRestaurant = useCallback(async () => {
    const lat = userLat ?? defaultCenter.lat;
    const lng = userLng ?? defaultCenter.lng;
    const apiBase = API_BASE_URL || "http://localhost:8080";
    console.log("[handleRandomRestaurant] start", { lat, lng, apiBase });
    try {
      const url = `${apiBase}/api/restaurants/search-restaurants?lat=${lat}&lng=${lng}&count=1`;
      console.log("[handleRandomRestaurant] fetch url:", url);
      const res = await fetch(url, { method: "GET" });
      console.log(
        "[handleRandomRestaurant] fetch response status:",
        res.status
      );
      const data = await res.json();
      console.log("[handleRandomRestaurant] fetch data:", data);
      if (
        data.status === "success" &&
        data.results &&
        data.results.length > 0
      ) {
        setRandomRestaurant(data.results[0]);
        console.log(
          "[handleRandomRestaurant] setRandomRestaurant",
          data.results[0]
        );
        // 取得餐廳後自動產生路線和 infocard
        if (data.results[0].geometry && data.results[0].geometry.location) {
          console.log(
            "[handleRandomRestaurant] call handleSetRoute",
            data.results[0].geometry.location
          );
          handleSetRoute({
            type: "destination",
            dest_lat: data.results[0].geometry.location.lat,
            dest_lng: data.results[0].geometry.location.lng,
            destination: data.results[0],
          });
        } else {
          console.warn(
            "[handleRandomRestaurant] result has no geometry.location"
          );
        }
      } else {
        setRandomRestaurant({
          name: "查無餐廳",
          description: data.message || "請稍後再試或更換地點。",
        });
        console.warn("[handleRandomRestaurant] 查無餐廳", data);
        // 查無餐廳時 infocard 不顯示
        handleSetRoute({ type: "destination", destination: null });
      }
    } catch (err) {
      setRandomRestaurant({
        name: "API 錯誤",
        description: "無法取得餐廳資料，請檢查網路或伺服器。",
      });
      setSelectedPlace(null);
      console.error("[handleRandomRestaurant] API error", err);
    }
  }, [userLat, userLng, handleSetRoute]);

  // --- 即時取得附近寶藏 ---
  useEffect(() => {
    if (userLat && userLng) {
      async function fetchNearbyTreasures() {
        try {
          const url = `${API_BASE_URL}/api/treasure/nearby?lat=${userLat}&lng=${userLng}&radius=2000`;
          const resp = await fetch(url);
          const data = await resp.json();
          if (data.treasures) {
            // 假設 MainContent 支援 treasures prop
            setNearbyTreasures(data.treasures);
          }
        } catch (e) {
          console.error("Failed to fetch nearby treasures", e);
        }
      }
      fetchNearbyTreasures();
    }
  }, [userLat, userLng]);

  return (
    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
      <div className="App font-sans bg-gray-50 min-h-screen w-screen h-screen overflow-hidden">
        <AppBar onLogin={() => alert("登入功能開發中")} />
        {/* 你可以在這裡顯示 mock 用戶資料 */}
        {/* {userData && (
          <div
            style={{
              position: "fixed",
              top: 60,
              right: 20,
              background: "#fff",
              padding: 12,
              borderRadius: 8,
              boxShadow: "0 2px 8px #0002",
              zIndex: 9999,
            }}
          >
            <div>暱稱: {userData.nickname}</div>
            <div>Email: {userData.email}</div>
            <div>餘額: {userData.balance}</div>
            <div>
              行動記錄:{" "}
              <pre style={{ margin: 0 }}>
                {JSON.stringify(userData.logs, null, 2)}
              </pre>
            </div>
          </div>
        )} */}
        <div className="h-14" />
        {showGeoLoading && (
          <LoadingOverlay
            text="正在定位，請稍候…"
            zIndex={2000}
            errorText={geoError}
            showRetry={!!geoError}
            onRetry={() => {
              setGeoError(null);
              setGeoRetryKey((k) => k + 1);
            }}
          />
        )}
        {loading && (
          <LoadingOverlay
            text="Generating route..."
            showCancel
            onCancel={handleCancelGenerate}
          />
        )}
        <MainContent
          mapKey={mapKey}
          containerStyle={containerStyle}
          mapCenter={mapCenter}
          onLoad={onLoad}
          onUnmount={onUnmount}
          mapStyles={mapStyles}
          places={places}
          routes={routes}
          selectedPlace={selectedPlace}
          setSelectedPlace={setSelectedPlace}
          userLat={userLat}
          userLng={userLng}
          heading={heading}
          directions={directions}
          handleMapClick={(e) => {
            if (e && e.latLng) {
              const lat = e.latLng.lat();
              const lng = e.latLng.lng();
              console.log("🗺️ 用戶點擊地圖位置:", {
                coordinates: { lat, lng },
                timestamp: new Date().toISOString(),
              });
              setSelectedPlace({
                name: "自定義位置",
                geometry: { location: { lat, lng } },
                isCustom: true,
                timestamp: new Date().toISOString(),
              });
            }
          }}
          getPlacePhotoUrl={getPlacePhotoUrl}
          loading={loading}
          shouldShowControls={shouldShowControls}
          error={error}
          duration={duration}
          setDuration={setDuration}
          handleGenerateRoute={handleGenerateRoute}
          handleSetRoute={handleSetRoute}
          setShowControls={setShowControls}
          setRoutes={setRoutes}
          setOriginLat={setOriginLat}
          setOriginLng={setOriginLng}
          setSelectedPlaceMain={setSelectedPlace}
          navStep={navStep}
          setNavStep={setNavStep}
          showNavigation={showNavigation}
          setShowNavigation={setShowNavigation}
          treasures={nearbyTreasures} // 新增 treasures prop
        />
        <FabGroup
          setShowControls={setShowControls}
          routes={routes}
          shouldShowControls={shouldShowControls}
          open={openFabGroup}
          setOpen={setOpenFabGroup}
          randomRestaurant={randomRestaurant}
          setRandomRestaurant={setRandomRestaurant}
          onRandomRestaurant={handleRandomRestaurant}
          onTreasure={handleFabTreasure}
          isLoggedIn={isLoggedIn}
        />
        {process.env.REACT_APP_DEBUG_TOOLS === "true" && (
          <DebugTools
            setUserLat={setUserLat}
            setUserLng={setUserLng}
            setHeading={setHeading}
            userLat={userLat}
            userLng={userLng}
          />
        )}
        <TreasurePrompt
          open={showTreasurePrompt}
          place={treasurePlace}
          onConfirm={handleConfirmTreasure}
          onCancel={() => {
            setShowTreasurePrompt(false);
            setTreasurePlace(null);
            setOpenFabGroup(true);
          }}
        />
        <TreasureExtraModal
          open={showTreasureExtra}
          onSubmit={handleTreasureExtraSubmit}
          onCancel={handleTreasureExtraCancel}
        />
        {showTreasureSuccess && (
          <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[99999] px-6 py-3 bg-amber-400 text-neutral-900 font-bold rounded-2xl shadow-xl text-lg flex items-center gap-2 animate-fadeIn">
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              className="mr-2"
            >
              <rect
                x="6"
                y="16"
                width="20"
                height="8"
                rx="3"
                fill="#c47f2b"
                stroke="#23272f"
                strokeWidth="2"
              />
              <rect
                x="6"
                y="10"
                width="20"
                height="7"
                rx="3.5"
                fill="#e2b86b"
                stroke="#23272f"
                strokeWidth="2"
              />
              <rect
                x="6"
                y="15"
                width="20"
                height="2"
                rx="1"
                fill="#bfa76a"
                opacity="0.5"
              />
              <rect
                x="14"
                y="20"
                width="4"
                height="4"
                rx="1"
                fill="#fffbe6"
                stroke="#23272f"
                strokeWidth="1.2"
              />
              <circle cx="16" cy="13" r="10" fill="url(#paint0_linear_1_2)" />
              <defs>
                <linearGradient
                  id="paint0_linear_1_2"
                  x1="16"
                  y1="3"
                  x2="16"
                  y2="23"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#ffdf80" />
                  <stop offset="1" stopColor="#ffc700" />
                </linearGradient>
              </defs>
            </svg>
            <div>恭喜！藏寶成功！</div>
          </div>
        )}
      </div>
    </LoadScript>
  );
}

export default App;
